import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  User,
  Mail,
  Lock,
  Phone,
  Building,
  Briefcase,
  AlertCircle,
  CheckCircle,
  Camera,
  Trash2,
  Edit3,
  Calendar,
  Users,
  Mic,
  ShieldCheck,
  Check,
} from "lucide-react";
import { authAPI } from "../services/api";
import Modal from "../components/Modal";
import "./Register.css";

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    aadhar_number: "",
    rfs_id: "",
    rf_batch: "",
    emergency_contact_guardian: "",
    participation_confirmed: "yes",
    accepted_terms: false,
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerUpload = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };

  const startCamera = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
      });
      setCameraStream(stream);
      setIsCameraOpen(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Could not access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      // Target a square crop from the center
      const size = Math.min(video.videoWidth, video.videoHeight);
      const startX = (video.videoWidth - size) / 2;
      const startY = (video.videoHeight - size) / 2;

      canvas.width = 400;
      canvas.height = 400;

      context.drawImage(video, startX, startY, size, size, 0, 0, 400, 400);

      canvas.toBlob(
        (blob) => {
          const file = new File([blob], "webcam-photo.jpg", {
            type: "image/jpeg",
          });
          setPhoto(file);
          setPhotoPreview(canvas.toDataURL("image/jpeg"));
          stopCamera();
        },
        "image/jpeg",
        0.9,
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!photo) {
      setError(
        "Profile photo is required. Please upload or take a photo to continue.",
      );
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append("email", formData.email);
      data.append("password", formData.password);
      data.append("full_name", `${formData.first_name} ${formData.last_name}`.trim());
      data.append("phone", formData.phone);
      data.append("aadhar_number", formData.aadhar_number);
      data.append("rfs_id", formData.rfs_id);
      data.append("rf_batch", formData.rf_batch);
      data.append("emergency_contact_guardian", formData.emergency_contact_guardian);
      data.append("participation_confirmed", formData.participation_confirmed === "yes");
      data.append("non_participation_reason", formData.no_reason || "");
      data.append("accepted_terms", formData.accepted_terms);
      data.append("avatar", photo);

      await authAPI.register(data);
      // On success, show custom success modal
      setShowSuccessModal(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors are usually an array of objects
        const firstError = detail[0];
        setError(
          typeof firstError === "object"
            ? `${firstError.msg || "Invalid input"} (${firstError.loc?.join(".") || ""})`
            : "Validation error",
        );
      } else if (detail && typeof detail === "object") {
        setError(detail.msg || "Registration failed");
      } else {
        setError(err.response?.data?.message || "Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-hero">
        <div className="hero-content">
          <h1>Join RF Conference</h1>
          <p>Create your attendee account to get started</p>
        </div>
      </div>

      <div className="register-content">
        <div className="register-form">
          {error && (
            <div className="error-message" style={{ margin: "20px 40px 0" }}>
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* 1. Profile Photo Section */}
            <div className="form-section photo-upload-section">
              <div className="section-header" style={{ alignSelf: "flex-start", width: "100%" }}>
                <div className="section-icon">
                  <Camera size={20} />
                </div>
                <h2>Profile Photo</h2>
              </div>
              
              <div className="photo-preview-large">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile Preview" />
                ) : (
                  <User size={60} />
                )}
              </div>

              <div className="photo-action-buttons">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
                {!photo ? (
                  <>
                    <button type="button" className="photo-action-btn" onClick={startCamera}>
                      <Camera size={16} /> Take Photo
                    </button>
                    <button type="button" className="photo-action-btn" onClick={triggerUpload}>
                      <User size={16} /> Gallery
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="photo-action-btn secondary" onClick={triggerUpload}>
                      <Edit3 size={16} /> Replace
                    </button>
                    <button type="button" className="photo-action-btn danger" onClick={removePhoto}>
                      <Trash2 size={16} /> Remove
                    </button>
                  </>
                )}
              </div>
              <p className="photo-upload-label">This photo will be used for your digital badge</p>
            </div>

            {/* 2. Account Basics */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <User size={20} />
                </div>
                <h2>Account Basics</h2>
              </div>
              
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Name <span className="input-required-mark">*</span></label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <div className="input-wrapper">
                        <User size={20} className="input-icon" />
                        <input
                          required
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        />
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>First Name</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="input-wrapper">
                        <input
                          required
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          style={{ paddingLeft: '16px' }}
                        />
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Last Name</div>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Registered Email ID <span className="input-required-mark">*</span></label>
                  <div className="input-wrapper">
                    <Mail size={20} className="input-icon" />
                    <input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Password <span className="input-required-mark">*</span></label>
                  <div className="input-wrapper">
                    <Lock size={20} className="input-icon" />
                    <input
                      required
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Academic Identity */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <ShieldCheck size={20} />
                </div>
                <h2>Academic Identity</h2>
              </div>
              
              <div className="form-grid-triple">
                <div className="form-group full-width">
                  <label>RFS ID <span className="input-required-mark">*</span></label>
                  <div className="input-wrapper">
                    <ShieldCheck size={20} className="input-icon" />
                    <input
                      required
                      type="text"
                      value={formData.rfs_id}
                      onChange={(e) => setFormData({ ...formData, rfs_id: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>Your 17 digit Application ID(For eg: RFSCH240000000000)</div>
                </div>

                <div className="form-group full-width">
                  <label>Aadhar Number <span className="input-required-mark">*</span></label>
                  <div className="input-wrapper">
                    <ShieldCheck size={20} className="input-icon" />
                    <input
                      required
                      type="text"
                      maxLength="12"
                      value={formData.aadhar_number}
                      onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value.replace(/\D/g, "") })}
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>RF Batch <span className="input-required-mark">*</span></label>
                  <div className="radio-group-vertical" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px', marginLeft: '4px' }}>
                    {[
                      "UG 20- 21", "UG 21-22", "UG 22-23", "UG 23-24", "UG 24-25", "UG 25-26",
                      "PG 20- 21", "PG 21-22", "PG 22-23", "PG 23-24", "PG 24-25", "PG 25-26"
                    ].map((batch) => (
                      <label key={batch} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: 400, color: '#333' }}>
                        <input
                          required
                          type="radio"
                          name="rf_batch"
                          value={batch}
                          checked={formData.rf_batch === batch}
                          onChange={(e) => setFormData({ ...formData, rf_batch: e.target.value })}
                          style={{ margin: 0, width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        {batch}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Contact & Organization */}
            <div className="form-section">
              <div className="section-header">
                <div className="section-icon">
                  <Phone size={20} />
                </div>
                <h2>Safety & Organizations</h2>
              </div>
              
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Mobile Number <span className="input-required-mark">*</span></label>
                  <div className="input-wrapper">
                    <Phone size={20} className="input-icon" />
                    <input
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Emergency Contact Number of Local Guardian <span className="input-required-mark">*</span></label>
                  <div className="input-wrapper">
                    <Phone size={20} className="input-icon" />
                    <input
                      required
                      type="tel"
                      value={formData.emergency_contact_guardian}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_guardian: e.target.value })}
                    />
                  </div>
                </div>



                <div className="form-group full-width" style={{ marginTop: '16px' }}>
                  <label>Will you be interested to join us for a half-day session in Delhi on 12th August 2026 <span className="input-required-mark">*</span></label>
                  <div className="radio-group-vertical" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px', marginLeft: '4px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: 400, color: '#333' }}>
                      <input
                        required
                        type="radio"
                        name="participation"
                        value="yes"
                        checked={formData.participation_confirmed === "yes"}
                        onChange={(e) => setFormData({ ...formData, participation_confirmed: e.target.value })}
                        style={{ margin: 0, width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      Yes
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontWeight: 400, color: '#333' }}>
                      <input
                        required
                        type="radio"
                        name="participation"
                        value="no"
                        checked={formData.participation_confirmed === "no"}
                        onChange={(e) => setFormData({ ...formData, participation_confirmed: e.target.value })}
                        style={{ margin: 0, width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      No
                    </label>
                  </div>
                  
                  {formData.participation_confirmed === "no" && (
                    <div style={{ marginTop: '20px' }}>
                      <label>If, NO reason for not able to attend the event <span className="input-required-mark">*</span></label>
                      <textarea
                        required
                        value={formData.no_reason || ""}
                        onChange={(e) => setFormData({ ...formData, no_reason: e.target.value })}
                        style={{ width: '100%', minHeight: '80px', marginTop: '8px', padding: '12px', borderRadius: '8px', border: '1px solid #ccc', outline: 'none' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Section */}
            <div className="form-footer">
              <label className="terms-box">
                <input
                  required
                  type="checkbox"
                  style={{ position: "absolute", opacity: 0 }}
                  checked={formData.accepted_terms}
                  onChange={(e) => setFormData({ ...formData, accepted_terms: e.target.checked })}
                />
                <div className="checkbox-visual"></div>
                <div className="terms-content">
                  <strong>I accept the Terms and Conditions <span className="input-required-mark">*</span></strong>
                  All personal expenses, including travel and any other incidental costs, must be borne by the participant.
                </div>
              </label>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
                style={{ height: "60px", fontSize: "1.1rem", borderRadius: "16px" }}
              >
                {loading ? <div className="btn-loading"></div> : "Complete Registration"}
              </button>

              <p className="help-text">
                Already have an account?{" "}
                <Link to="/login" className="text-primary" style={{ fontWeight: 600 }}>
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Modals remain mostly same but integrated into styling */}
      <Modal isOpen={showSuccessModal} onClose={() => navigate("/login")} showClose={false}>
        <div className="success-modal-content" style={{ textAlign: "center", padding: "20px" }}>
          <div className="success-icon-wrapper" style={{ 
            width: "64px", height: "64px", background: "var(--rf-success)", 
            color: "white", borderRadius: "50%", display: "flex", 
            alignItems: "center", justifyContent: "center", margin: "0 auto 20px" 
          }}>
            <CheckCircle size={32} />
          </div>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "12px" }}>Registration Successful!</h2>
          <p style={{ color: "var(--rf-gray-600)", marginBottom: "32px" }}>
            Your account has been created. You can now sign in to your dashboard.
          </p>
          <button className="btn btn-primary btn-block" onClick={() => navigate("/login")}>
            Go to Login
          </button>
        </div>
      </Modal>

      <Modal isOpen={isCameraOpen} onClose={stopCamera} title="Take Profile Photo">
        <div className="camera-modal-content">
          <div className="video-container">
            <video
              ref={(el) => {
                videoRef.current = el;
                if (el && cameraStream) {
                  el.srcObject = cameraStream;
                  el.play().catch((e) => console.error("Error playing video:", e));
                }
              }}
              autoPlay playsInline muted
            />
          </div>
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <div className="camera-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={stopCamera} style={{ flex: 1 }}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={capturePhoto} style={{ flex: 1 }}>
              <Camera size={18} /> Capture
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Register;
